// ─── coachPush ───────────────────────────────────────────────────────────
// 2026-07-26 — Push Anissa (phase 2 Suivi mobile) : abonnement Web Push de
// l'appareil d'Anissa. Quand une cliente partage un ressenti ou écrit un
// message, l'app cliente (sendPushToCoach) notifie tous les appareils
// abonnés ici. Les abonnements vivent dans coach_push_subscriptions côté
// app cliente, gérés via /api/admin/coach-push (proxy server-side, secret
// jamais dans le bundle).
//
// Note iOS : le push web n'existe que si le cockpit est installé sur
// l'écran d'accueil (PWA, iOS 16.4+) — et la demande de permission doit
// partir d'un geste utilisateur (le bouton du Suivi).

import { clientAppFetch } from "./clientAppFetch";

/** Décodage clé VAPID base64url → Uint8Array pour pushManager.subscribe. */
export function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

/** Étiquette lisible de l'appareil courant, pour la liste côté API. */
export function defaultDeviceLabel(ua) {
  const s = ua || (typeof navigator !== "undefined" ? navigator.userAgent : "") || "";
  if (/iPhone/i.test(s)) return "iPhone";
  if (/iPad/i.test(s)) return "iPad";
  if (/Android/i.test(s)) return "Android";
  if (/Macintosh/i.test(s)) return "Mac";
  if (/Windows/i.test(s)) return "PC";
  return "Appareil";
}

/**
 * État du support push sur cet appareil :
 * 'unsupported' | 'denied' | 'default' | 'granted'
 */
export function pushSupportState() {
  if (
    typeof navigator === "undefined" ||
    !("serviceWorker" in navigator) ||
    typeof window === "undefined" ||
    !("PushManager" in window) ||
    !("Notification" in window)
  ) {
    return "unsupported";
  }
  return Notification.permission;
}

/** Registration SW ou null (en dev le SW n'est pas enregistré). */
async function getRegistration() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return (await navigator.serviceWorker.getRegistration()) || null;
  } catch {
    return null;
  }
}

/** L'appareil courant est-il déjà abonné ? */
export async function isSubscribed() {
  const reg = await getRegistration();
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  return !!sub;
}

/**
 * Abonne cet appareil : permission → clé VAPID (via proxy) → subscribe →
 * enregistrement côté app cliente. Lève des Error à message court pour
 * l'affichage ('permission_refusee', 'sw_absent').
 */
export async function subscribeCoachPush() {
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("permission_refusee");

  const reg = await getRegistration();
  if (!reg) throw new Error("sw_absent");

  const info = await clientAppFetch("/api/admin/coach-push", { method: "GET" });
  const vapidKey = info?.vapid_public_key;
  if (!vapidKey) throw new Error("cle_vapid_manquante");

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });
  }

  const json = sub.toJSON();
  await clientAppFetch("/api/admin/coach-push", {
    method: "POST",
    payload: {
      endpoint: sub.endpoint,
      p256dh: json.keys?.p256dh,
      auth: json.keys?.auth,
      device_label: defaultDeviceLabel(),
    },
  });
  return true;
}

/** Désabonne cet appareil (suppression côté API best-effort d'abord). */
export async function unsubscribeCoachPush() {
  const reg = await getRegistration();
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return false;
  try {
    await clientAppFetch("/api/admin/coach-push", {
      method: "DELETE",
      payload: { endpoint: sub.endpoint },
    });
  } catch {
    // best-effort : même si l'API échoue, on libère l'appareil ;
    // l'abonnement mort sera nettoyé au prochain envoi (410).
  }
  await sub.unsubscribe();
  return true;
}
